import type { TFunction } from 'i18next';

export interface PriceCard {
  id: string;
  title: string;
  price: string;
  features?: string[];
  imagePath?: string;
}

export interface ServicePricing {
  title: string;
  cards: PriceCard[];
  backgroundColor: string;
  buttonClassName: string;
  includedServices?: string[];
  excludedServices?: string[];
}

// Factory function to get service pricing with translations
export const getServicePricing = (t: TFunction): Record<string, ServicePricing> => {
  // Service key mapping for pricing
  const pricingKeyMap: Record<string, string> = {
    'car-polish': 'carPolish',
    'aircond': 'aircond',
    'fabric': 'fabric',
    'sanitise': 'sanitise',
    'pest-control': 'pestControl'
  };

  const pricing: Record<string, ServicePricing> = {};

  // Helper function to get pricing data with translations
  const getPricingForService = (serviceId: string): ServicePricing | null => {
    const key = pricingKeyMap[serviceId];
    if (!key) return null;

    const pricingData = t(`pricing.${key}`, { returnObjects: true }) as any;

    // Get background color and button class based on service
    const styleConfig: Record<string, { bg: string; btn: string }> = {
      'car-polish': {
        bg: 'bg-black',
        btn: 'bg-trhine-red-100 text-white px-6 py-2 rounded-md text-[14px] hover:opacity-90'
      },
      'aircond': {
        bg: 'bg-trhine-blue-100',
        btn: 'bg-trhine-blue-100 text-white px-6 py-2 rounded-md text-[14px] hover:opacity-90'
      },
      'fabric': {
        bg: 'bg-trhine-yellow-200',
        btn: 'bg-trhine-yellow-300 text-black px-6 py-2 rounded-md text-[14px] hover:opacity-90'
      },
      'sanitise': {
        bg: 'bg-gradient-to-br from-trhine-green-100 to-trhine-green-200',
        btn: 'bg-trhine-green-100 text-white px-6 py-2 rounded-md text-[14px] hover:opacity-90'
      },
      'pest-control': {
        bg: 'bg-gradient-to-br from-trhine-brown-100 to-trhine-brown-200',
        btn: 'bg-trhine-brown-100 text-white px-6 py-2 rounded-md text-[14px] hover:opacity-90'
      }
    };

    const styles = styleConfig[serviceId];

    return {
      title: pricingData.title,
      backgroundColor: styles.bg,
      buttonClassName: styles.btn,
      includedServices: pricingData.includedServices,
      excludedServices: pricingData.excludedServices,
      cards: pricingData.cards || []
    };
  };

  // Generate pricing for all services
  Object.keys(pricingKeyMap).forEach(serviceId => {
    const pricingForService = getPricingForService(serviceId);
    if (pricingForService) {
      pricing[serviceId] = pricingForService;
    }
  });

  return pricing;
};
